import { ORDER_METHODS } from '../exports/Constants';
import { isOnSiteMethod } from './OrderMethodAliases';

/**
 * How a table order's table is shown to staff (PROD-724). Pure, so the orders list and the
 * order drawer read it the same way. See tests/services/helpers/OnSiteOrder.test.js.
 */

/** One space between words and none at the ends, as the backend stores a table name. */
export function squish(text) {
  return String(text ?? '')
    .trim()
    .replace(/\s+/g, ' ');
}

/** Whether a table name reads as a number ("7", "1A"), which is shown as "Table 7". */
export function isNumberedTableName(name) {
  return /^\d/.test(squish(name));
}

/**
 * The table as people are told it: "Table 7" for a name that starts with a digit, a name like
 * "Terrace 2" as it is. The backend (`OnSiteTable::labelFor()`) and the shop apply the same rule.
 * `numbered` builds the "Table {{name}}" text in the dashboard's language.
 */
export function tableLabel(name, numbered) {
  const clean = squish(name);

  if (!clean) {
    return '';
  }

  return isNumberedTableName(clean) ? numbered(clean) : clean;
}

/**
 * What an order says about its table. `line` is `on_site_table_name`: the table line as it was
 * placed, frozen at checkout ("7", "Terrasse · 7"), so a table renamed since doesn't change it. It
 * is never split: a table's name may contain " · " itself ("Bar · 2"). `on_site_table` is the live
 * table, under its current name, so it never labels an order.
 *
 * An order from Room/Table service carries the number the guest typed in the deprecated
 * `room_service_details` (null for table orders). Its venue is the line without that number, when
 * the line ends with it ("Hotel Lindenhof · 12" and "12").
 */
export function onSiteOrderTable(order) {
  const line = squish(order?.on_site_table_name) || null;
  const typed = squish(order?.room_service_details) || null;

  if (!typed) {
    return { line, venue: null, typed: null };
  }

  const typedSuffix = ` · ${typed}`;
  const venue = line?.endsWith(typedSuffix) ? line.slice(0, -typedSuffix.length) || null : null;

  return { line, venue, typed };
}

/**
 * The order methods the payment-methods form sets options for. Table orders take pickup's options,
 * which the backend copies onto `on_site` on save, so the form has no column for them.
 */
export const PAYMENT_RULE_ORDER_METHODS = [ORDER_METHODS.pickup, ORDER_METHODS.delivery];

/** `hidden_payment_methods` without the table-ordering copy, as the payment-methods form edits it. */
export function withoutOnSitePaymentRules(map) {
  if (!map || Array.isArray(map) || typeof map !== 'object') {
    return map;
  }

  return Object.fromEntries(
    Object.entries(map)
      .map(([paymentMethod, orderMethods]) => [
        paymentMethod,
        Array.isArray(orderMethods) ? orderMethods.filter((method) => !isOnSiteMethod(method)) : orderMethods,
      ])
      .filter(([, orderMethods]) => !Array.isArray(orderMethods) || orderMethods.length > 0),
  );
}
